import RequestStatusTypes from '../constants/RequestStatusTypes';
import I18n from 'I18n';
import createFluxAction from 'flux-actions/createAction';
import { createSuccessNotification, createDangerNotification } from 'ReduxMessenger/utils';
export const REQUEST = 'REQUEST';
export const SUCCESS = 'SUCCESS';
export const FAILURE = 'FAILURE';

// TODO Change to Record<RequestSuffix, `${Base}_${RequestSuffix}`> after eslint & prettier are updated to support TS4.3

export function createAction(type, actionCreator, metaCreator) {
  const _actionCreator = createFluxAction(type, actionCreator, metaCreator);
  _actionCreator.toString = function () {
    return type;
  };
  return _actionCreator;
}
export function createRequestTypes(base) {
  return {
    base,
    [REQUEST]: `${base}_${REQUEST}`,
    [SUCCESS]: `${base}_${SUCCESS}`,
    [FAILURE]: `${base}_${FAILURE}`
  };
}
export function createRequestStatusMeta(key, requestStatus) {
  return {
    request: {
      key,
      status: requestStatus
    }
  };
}
export function createSuccessNotificationMeta(message) {
  return {
    notification: createSuccessNotification(I18n.text('settingsui.notifications.success'), message, false)
  };
}
export function createDangerNotificationMeta(message) {
  return {
    notification: createDangerNotification(I18n.text('settingsui.notifications.error'), message, true)
  };
}
export const encodePropKeys = (props, keys = []) => {
  if (!props || keys.length === 0 || typeof props !== 'object') {
    return '';
  }
  return keys.reduce((acc, key) => {
    return `${acc}&${key}=${props[key]}`;
  }, '');
};
export function createRequestActions(resource, {
  keys,
  requestPayloadCreator = props => {
    return {
      data: props
    };
  },
  requestMetaCreator = props => createRequestStatusMeta(`${resource.base}${encodePropKeys(props, keys)}`, RequestStatusTypes.PENDING),
  successPayloadCreator = (response, props) => ({
    data: response,
    props
  }),
  successMetaCreator = (response, props) => createRequestStatusMeta(`${resource.base}${encodePropKeys(props, keys)}`, RequestStatusTypes.SUCCEEDED),
  failurePayloadCreator = (error, props) => ({
    error,
    props
  }),
  failureMetaCreator = (error, props) => createRequestStatusMeta(`${resource.base}${encodePropKeys(props, keys)}`, RequestStatusTypes.FAILED)
} = {}) {
  return {
    request: createAction(`${resource.base}_${REQUEST}`, requestPayloadCreator, requestMetaCreator),
    success: createAction(`${resource.base}_${SUCCESS}`, successPayloadCreator, successMetaCreator),
    failure: createAction(`${resource.base}_${FAILURE}`, failurePayloadCreator, failureMetaCreator)
  };
}

/**
 * Example usage:
 * ```
 * const example = mapPayloadTo('one', 'two', 'three');
 * const payload = example({ one: 1 }, { two: 2 }, { three: 3 });
 * ```
 * then typeof payload is:
 * ```
 *   { one: { 1: number } } &
 *   { two: { 2: number } } &
 *   { three: { 3: number };
 * ```
 *
 * @deprecated While the type is technically correct when used in isolation, it
 * will be lost when combined with something like `createRequestActions` because
 * TypeScript does not support Higher Kinded Types. Since this is a trivial
 * utility with some overhead and limitations, consider migrating it to manually
 * declared functions, e.g.:
 * ```
 * mapPayloadTo('property', 'value');
 * ```
 * becomes simply:
 * ```
 * // Remember to keep the original order of arguments
 * (property: string, value: string) => ({ property, value });
 * ```
 */
export function mapPayloadTo(...keys) {
  return (...actionCreatorArgs) => {
    return keys.reduce((acc, key, idx) => Object.assign({}, acc, {
      [key]: actionCreatorArgs[idx]
    }), {});
  };
}
export function createAsyncThunk(typePrefix, payloadCreator) {
  const actionTypes = createRequestTypes(typePrefix);
  const actions = createRequestActions(actionTypes);
  const {
    request,
    success,
    failure
  } = actions;
  function actionCreator(...[arg]) {
    return function thunk(dispatch, getState) {
      const action = request(arg);
      dispatch(action);
      return payloadCreator(arg, {
        dispatch,
        getState
      }).then(success, failure).then(dispatch);
    };
  }
  return Object.assign(actionCreator, actions);
}